import {playerActionTypes} from "../_constants/actions/analytics/playerActionTypes";
import {userService} from "../_services";

export const playerActions = {
	onLoad,
	onPlayPauseBtn,
	onSkipBtn,
	onVolumeChange,
	onSpeedChange,
	onFullViewChange,
	onGistViewToggle,
	onGistLogoRedirect,
	onGistWordPlay,
	onGistHintShow,
	onChapterChange,
	onTocChange,
	onTocChapterChange,
	onSearchChange,
	onSearchPerform
};

/**
 * Redux action method that gets invoked on gist-player loads with content
 *
 * @returns {function(...[*]=)}
 */
function onLoad() {
	return dispatch => {
		const userType = userService.getAuthUser() ? 'Subscriber' : 'Public';
		// utility constants - contains all module-related dispatches from this function
		const dispatchPlayerMediaLoad = () => dispatch({type: playerActionTypes.PLAYER_PLAY_LOAD, userType});

		dispatchPlayerMediaLoad();
	}
}

/**
 * Redux action method that gets invoked on-click event of play/pause buttons on the player
 *
 * @param isPlayBtn flag indicating if play/pause; true=play, false=pause
 * @returns {function(...[*]=)}
 */
function onPlayPauseBtn(isPlayBtn) {
	return dispatch => {
		const dispatchPlayerMediaResume = () => dispatch({type: playerActionTypes.PLAYER_PLAY_RESUME});
		const dispatchPlayerMediaPause = () => dispatch({type: playerActionTypes.PLAYER_PLAY_PAUSE});

		if(isPlayBtn) { dispatchPlayerMediaResume(); }
		else          { dispatchPlayerMediaPause(); }
	}
}

/**
 * Redux action method that gets invoked on-click event of the back/forward skip buttons or tap-based on mobile
 *
 * @param isForwardBtn flag indicating if forward/back seek; true=forward, false=back
 * @returns {function(...[*]=)}
 */
function onSkipBtn(isForwardBtn) {
	return dispatch => {
		const dispatchPlayerSkipForward = () => dispatch({type: playerActionTypes.PLAYER_SKIP_FORWARD});
		const dispatchPlayerSkipBackward = () => dispatch({type: playerActionTypes.PLAYER_SKIP_BACKWARD});

		if(isForwardBtn)  { dispatchPlayerSkipForward(); }
		else              { dispatchPlayerSkipBackward(); }
	}
}

/**
 * Redux action method that gets invoked on volume change
 *
 * @param volume the level of volume on the scale of 0 (lowest) to 1 (highest);
 * @returns {function(...[*]=)}
 */
function onVolumeChange(volume) {
	return dispatch => {
		const dispatchPlayerMediaVolumeChange = () => dispatch({type: playerActionTypes.PLAYER_VOLUME_CHANGE, volume});

		dispatchPlayerMediaVolumeChange();
	}
}

/**
 * Redux action method that gets invoked speed change
 *
 * @param speed the speed of volume; normal:1, slower:<1, faster:>1
 * @returns {function(...[*]=)}
 */
function onSpeedChange(speed) {
	return dispatch => {
		const dispatchPlayerMediaSpeedChange = () => dispatch({type: playerActionTypes.PLAYER_SPEED_CHANGE, speed});

		dispatchPlayerMediaSpeedChange();
	}
}

/**
 * Redux action method that gets invoked on full-screen enter or exit using player buttons.
 *
 * @param isFullView flag indicating if full-screen enter or exit; true=enter, false=exit
 * @returns {function(...[*]=)}
 */
function onFullViewChange(isFullView) {
	return dispatch => {
		const dispatchPlayerFullViewLoad = () => dispatch({type: playerActionTypes.PLAYER_FULL_VIEW_LOAD});
		const dispatchPlayerFullViewExit = () => dispatch({type: playerActionTypes.PLAYER_FULL_VIEW_EXIT});

		if(isFullView)  { dispatchPlayerFullViewLoad(); }
		else            { dispatchPlayerFullViewExit(); }
	}
}

/**
 * Redux action method that gets invoked on toggling the gist view
 *
 * @param isShow flag indicating if the gist view was shown or hidden; true=show, false=hide
 * @returns {function(...[*]=)}
 */
function onGistViewToggle(isShow) {
	return dispatch => {
		const dispatchPlayerGistViewShow = () => dispatch({type: playerActionTypes.PLAYER_GIST_SHOW});
		const dispatchPlayerGistViewHide = () => dispatch({type: playerActionTypes.PLAYER_GIST_HIDE});

		if(isShow)  { dispatchPlayerGistViewShow(); }
		else        { dispatchPlayerGistViewHide(); }
	}
}

/**
 * Redux action method that gets invoked when the gist logo on the player is clicked
 *
 * @returns {function(...[*]=)}
 */
function onGistLogoRedirect() {
	return dispatch => {
		const dispatchPlayerGistLogoRedirect = () => dispatch({type: playerActionTypes.PLAYER_GIST_LOGO_REDIRECT});

		dispatchPlayerGistLogoRedirect();
	}
}

/**
 * Redux action method that gets invoked when the gist word is clicked for playback
 *
 * @param weight the weight of the current word
 * @returns {function(...[*]=)}
 */
function onGistWordPlay(weight) {
	return dispatch => {
		const dispatchPlayerWordPlay = () => dispatch({type: playerActionTypes.PLAYER_GIST_WORD_PLAY, weight});

		dispatchPlayerWordPlay();
	}
}

/**
 * Redux action method that gets invoked when the gist hint gets displayed on the screen
 *
 * @returns {function(...[*]=)}
 */
function onGistHintShow() {
	return dispatch => {
		const dispatchGistHintShow = () => dispatch({type: playerActionTypes.PLAYER_GIST_HINT_SHOW});

		dispatchGistHintShow();
	}
}

/**
 * Redux action method that gets invoked during chapter navigation using swipe or click-nav
 *
 * @param invokerType represents the invoker-type, covers swipe and click-nav events
 * @returns {function(...[*]=)}
 */
function onChapterChange(invokerType) {
	return dispatch => {
		const dispatchPlayerChapterChange = () => dispatch({type: playerActionTypes.PLAYER_CHAPTER_CHANGE, invokerType});

		dispatchPlayerChapterChange();
	}
}

/**
 * Redux action method that gets invoked when the toc view gets loaded/closed
 *
 * @param isLoad flag indicating if the toc view is seen/hidden; true=loaded, false=closed
 * @returns {function(...[*]=)}
 */
function onTocChange(isLoad) {
	return dispatch => {
		const dispatchPlayerTocViewLoad = () => dispatch({type: playerActionTypes.PLAYER_TOC_VIEW_LOAD});
		const dispatchPlayerTocViewClose = () => dispatch({type: playerActionTypes.PLAYER_TOC_VIEW_CLOSE});

		if(isLoad)  { dispatchPlayerTocViewLoad(); }
		else        { dispatchPlayerTocViewClose(); }
	}
}

/**
 * Redux action method that gets invoked when the toc-chapter is clicked
 *
 * @returns {function(...[*]=)}
 */
function onTocChapterChange() {
	return dispatch => {
		const dispatchPlayerTocChapterChange = () => dispatch({type: playerActionTypes.PLAYER_TOC_VIEW_TO_CHAPTER});

		dispatchPlayerTocChapterChange();
	}
}

/**
 * Redux action method that gets invoked when the search is toggled
 *
 * @param isLoad indicates search is opened or closed
 * @returns {function(...[*]=)}
 */
function onSearchChange(isLoad) {
	return dispatch => {
		const dispatchPlayerSearchOpen = () => dispatch({type: playerActionTypes.PLAYER_SEARCH_OPEN});
		const dispatchPlayerSearchClose = () => dispatch({type: playerActionTypes.PLAYER_SEARCH_CLOSE});

		if(isLoad)  { dispatchPlayerSearchOpen(); }
		else        { dispatchPlayerSearchClose(); }
	}
}

/**
 * Redux action method that gets invoked when a new search is performed
 *
 * @returns {function(...[*]=)}
 */
function onSearchPerform() {
	return dispatch => {
		const dispatchPlayerSearchPerform = () => dispatch({type: playerActionTypes.PLAYER_SEARCH_PERFORM});
		dispatchPlayerSearchPerform();
	}
}